import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back button */}
        <Button variant="ghost" className="mb-6" data-testid="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="mr-2" />
          {t('terms.backToHome')}
        </Button>

        <Card data-testid="terms-of-service-card">
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-3xl font-heading">{t('terms.title')}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t('terms.lastUpdated')}</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-8">
            <div className="prose prose-sm max-w-none space-y-6">

              {/* Acceptance of Terms */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section1Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section1P1')}
                </p>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('terms.section1P2')}
                </p>
              </section>

              {/* PESO Panglao partnership */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.pesoTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.pesoBody')}
                </p>
              </section>

              {/* About the Platform */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section2Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section2P1')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
                  <li>{t('terms.section2_employers')}</li>
                  <li>{t('terms.section2_seekers')}</li>
                  <li>{t('terms.section2_clients')}</li>
                  <li>{t('terms.section2_contractors')}</li>
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3"
                   dangerouslySetInnerHTML={{ __html: t('terms.section2Important') }} />
              </section>

              {/* Account Registration */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section3Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.section3Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section3_accurate')}</li>
                  <li>{t('terms.section3_maintain')}</li>
                  <li>{t('terms.section3_password')}</li>
                  <li>{t('terms.section3_responsible')}</li>
                  <li>{t('terms.section3_notify')}</li>
                  <li>{t('terms.section3_socialRegister')}</li>
                  <li>{t('terms.section3_socialSecurity')}</li>
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('terms.section3Age')}
                </p>
              </section>

              {/* Social Login */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.socialLoginTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.socialLoginDesc')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.socialLogin_agreeTerms')}</li>
                  <li>{t('terms.socialLogin_notResponsible')}</li>
                  <li>{t('terms.socialLogin_disconnect')}</li>
                </ul>
              </section>

              {/* User Conduct */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section4Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.section4Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section4_falseListings')}</li>
                  <li>{t('terms.section4_impersonation')}</li>
                  <li>{t('terms.section4_harassment')}</li>
                  <li>{t('terms.section4_illegalContent')}</li>
                  <li>{t('terms.section4_collectFees')}</li>
                  <li>{t('terms.section4_illegalRecruitment')}</li>
                  <li>{t('terms.section4_scraping')}</li>
                  <li>{t('terms.section4_circumventing')}</li>
                  <li>{t('terms.section4_malware')}</li>
                  <li>{t('terms.section4_multipleAccounts')}</li>
                </ul>
              </section>

              {/* Job Postings and Applications */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section5Title')}</h2>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('terms.section5_1Title')}</h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section5_1_accurate')}</li>
                  <li>{t('terms.section5_1_comply')}</li>
                  <li>{t('terms.section5_1_nondiscrim')}</li>
                  <li>{t('terms.section5_1_verify')}</li>
                  <li>{t('terms.section5_1_expire')}</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('terms.section5_2Title')}</h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section5_2_truthful')}</li>
                  <li>{t('terms.section5_2_responsible')}</li>
                  <li>{t('terms.section5_2_professional')}</li>
                </ul>
              </section>

              {/* Gigs and Bidding */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section6Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.section6Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section6_accurate')}</li>
                  <li>{t('terms.section6_binding')}</li>
                  <li>{t('terms.section6_between')}</li>
                  <li>{t('terms.section6_notResponsible')}</li>
                </ul>
              </section>

              {/* Credits and Payments */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section7Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.section7Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section7_unlock')}</li>
                  <li>{t('terms.section7_noRefund')}</li>
                  <li>{t('terms.section7_valid')}</li>
                  <li>{t('terms.section7_noTransfer')}</li>
                  <li>{t('terms.section7_promo')}</li>
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('terms.section7PaymentNote')}
                </p>
              </section>

              {/* ID Verification */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section8Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section8Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
                  <li>{t('terms.section8_validId')}</li>
                  <li>{t('terms.section8_badge')}</li>
                  <li>{t('terms.section8_noGuarantee')}</li>
                  <li>{t('terms.section8_revoke')}</li>
                </ul>
              </section>

              {/* SMS Communications */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.smsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.smsIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.sms_receive')}</li>
                  <li>{t('terms.sms_optOut')}</li>
                  <li>{t('terms.sms_carrierCharges')}</li>
                  <li>{t('terms.sms_provider')}</li>
                </ul>
              </section>

              {/* Resume Upload + AI Parsing */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.aiResumeTitle', 'Resume Upload and AI Parsing')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.aiResumeIntro', 'You may upload a resume to your profile. We use AI (Anthropic Claude) to extract profile information automatically. AI extraction may be imperfect — you are responsible for reviewing and correcting the auto-filled fields before saving. You may delete your resume from your profile at any time.')}
                </p>
              </section>

              {/* Authorized Partner Organizations */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.pesoTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.pesoIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.peso_anonymized')}</li>
                  <li>{t('terms.peso_consent')}</li>
                </ul>
              </section>

              {/* Analytics and Session Recording */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.analyticsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.analyticsDesc')}
                </p>
              </section>

              {/* Referral Program */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.referralTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.referralIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.referral_eligibility')}</li>
                  <li>{t('terms.referral_rewards')}</li>
                  <li>{t('terms.referral_gcash')}</li>
                  <li>{t('terms.referral_abuse')}</li>
                  <li>{t('terms.referral_terms')}</li>
                </ul>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section9Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section9P1')}
                </p>
              </section>

              {/* Disclaimers */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section10Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.section10Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.section10_employment')}</li>
                  <li>{t('terms.section10_accuracy')}</li>
                  <li>{t('terms.section10_quality')}</li>
                  <li>{t('terms.section10_availability')}</li>
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('terms.section10Liability')}
                </p>
              </section>

              {/* Indemnification */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section11Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section11P1')}
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section12Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section12P1')}
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section13Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section13P1')}
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.section14Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('terms.section14Intro')}
                </p>
                <div className="mt-3 p-4 bg-muted rounded-lg">
                  <p className="text-sm"><strong>{t('terms.contactName')}</strong></p>
                  <p className="text-sm text-muted-foreground">Email: <a href="mailto:support@boholtrabaho.com" className="text-primary hover:underline">{t('terms.contactEmail')}</a></p>
                  <p className="text-sm text-muted-foreground">Website: <a href="https://www.boholtrabaho.com" className="text-primary hover:underline">{t('terms.contactWebsite')}</a></p>
                </div>
              </section>

              {/* Services Marketplace */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('terms.services_introOnly') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.services_noEscrow') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.services_offline') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.services_noGuarantee') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.services_ownRisk') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('terms.services_free')}
                </p>
              </section>

              {/* Reviews and Ratings */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesReviewsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesReviewsIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesReviews_verified') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesReviews_blind') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesReviews_unconfirmed') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesReviews_cyberLibel') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesReviews_noTakedown') }} />
                </ul>
              </section>

              {/* Verification & Credentials */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesVerifiedTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesVerifiedIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesVerified_identityOnly') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesVerified_selfProvided') }} />
                  <li>{t('terms.servicesVerified_clientResponsibility')}</li>
                </ul>
              </section>

              {/* Prohibited and Restricted Services */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesProhibitedTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesProhibitedIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.servicesProhibited_illegal')}</li>
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesProhibited_adult') }} />
                  <li>{t('terms.servicesProhibited_weapons')}</li>
                  <li>{t('terms.servicesProhibited_medical')}</li>
                  <li>{t('terms.servicesProhibited_lawViolation')}</li>
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3 mb-2"
                   dangerouslySetInnerHTML={{ __html: t('terms.servicesRestrictedIntro') }} />
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesRestricted_childcare') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('terms.servicesRestricted_massage') }} />
                </ul>
              </section>

              {/* Internet Transactions Act */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesItaTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesItaIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.servicesIta_merchantDetails')}</li>
                  <li>{t('terms.servicesIta_complaintChannel')}</li>
                  <li>{t('terms.servicesIta_noConclude')}</li>
                </ul>
              </section>

              {/* Service Requests */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('terms.servicesRequestsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('terms.servicesRequestsIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('terms.servicesRequests_visibility')}</li>
                  <li>{t('terms.servicesRequests_limits')}</li>
                  <li>{t('terms.servicesRequests_noSms')}</li>
                  <li>{t('terms.servicesRequests_reveal')}</li>
                </ul>
              </section>

            </div>

            {/* Related Links */}
            <div className="mt-8 p-4 bg-muted rounded-lg">
              <h3 className="font-semibold mb-2">{t('terms.relatedPolicies')}</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/privacy">
                  <Button variant="outline" size="sm">
                    {t('terms.privacyPolicy')}
                  </Button>
                </Link>
                <Link to="/delete-data">
                  <Button variant="outline" size="sm">
                    {t('terms.dataDeletion')}
                  </Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
